import {Box, Link, Typography, Tooltip, Button} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {capitalizeWords} from '@utils';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {LinkedIn, Twitter, Facebook} from '@jobseeker/lotus/assets/icons';
import {VersionVariants} from '@jobseeker/lotus/enums';
import dynamic from 'next/dynamic';
import {ProfileType} from '@jobseeker/common-core/services/types';

const ProfileCardHarness = dynamic(
  () => import('@jobseeker/client-harness/components/ProfileHarness'),
  {
    ssr: false,
  }
);

const ABS_URL = 'https://static.iimjobs.com/';

const ProfileSection = ({
  profileInfo,
  onSocialClick,
}: {
  profileInfo: ProfileType;
  onSocialClick: (
    type: 'Twitter' | 'Facebook' | 'LinkedIn',
    url: string
  ) => void;
}) => {
  const imageUrl =
    profileInfo?.profilePicPath ||
    `${ABS_URL}resources/img/user_profile_new.png`;
  const name = profileInfo?.name;
  const email = profileInfo?.email;
  const phone = profileInfo?.phone;

  let verifyButton = '';
  if (!profileInfo?.verifiedEmail && !profileInfo?.verifiedNumber) {
    verifyButton = 'Verify your Contact Details';
  } else if (!profileInfo?.verifiedEmail && profileInfo?.verifiedNumber) {
    verifyButton = 'Verify Your Email Address';
  } else if (profileInfo?.verifiedEmail && !profileInfo?.verifiedNumber) {
    verifyButton = 'Verify Your Phone Number';
  }

  let expyear = profileInfo?.experienceYear
    ? String(profileInfo.experienceYear)
    : '0';
  let expmonth = profileInfo?.experienceMonth
    ? String(profileInfo.experienceMonth)
    : '0';
  expyear = expyear == '999' ? '0' : expyear;
  expmonth = expmonth == '999' ? '0' : expmonth;

  if (Number(expyear) > 1) {
    expyear = expyear + ' years';
  } else {
    expyear = expyear + ' year';
  }

  if (Number(expmonth) > 1) {
    expmonth = expmonth + ' months';
  } else {
    expmonth = expmonth + ' month';
  }

  const location = profileInfo?.locations?.current?.name
    ? profileInfo.locations.current.name
    : '';

  return (
    <Box sx={{p: '5px 15px 0px'}}>
      <Box sx={{textAlign: 'center'}}>
        <Box sx={{m: 0, p: '15px 0px'}}>
          <span
            style={{width: '110px', height: '110px', display: 'inline-block'}}
          >
            <Link
              href="/registration/addPersonalDetails"
              sx={{color: '#4b4b4b', textDecoration: 'none'}}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  border: '2px solid #fff',
                  bgcolor: '#78e184',
                  borderRadius: '50%',
                  position: 'relative',
                  top: '88px',
                  left: '97px',
                }}
              ></Box>
              <Image
                src={imageUrl}
                alt="user-icon"
                width={110}
                height={110}
                style={{
                  borderRadius: '50%',
                  overflow: 'hidden',
                  transition: '1s',
                }}
              />
            </Link>
          </span>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 600,
              m: 0,
              p: 0,
              wordBreak: 'break-all',
            }}
          >
            {capitalizeWords(name)}
            <span
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                fontSize: 13,
                paddingTop: '3px',
                lineHeight: '24px',
                fontWeight: 400,
              }}
            >
              {email}
              {profileInfo?.verifiedEmail === 1 && (
                <Tooltip
                  placement="top"
                  title={
                    <Typography
                      variant="subtitle2"
                      sx={{
                        textAlign: 'center',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                        backgroundColor: 'black',
                        color: '#fff',
                        px: '2px',
                        borderRadius: '4px',
                        fontSize: 10,
                        fontWeight: 400,
                      }}
                    >
                      Email Address Verified
                    </Typography>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'black',
                        color: '#fff',
                        p: '4px',
                      },
                    },
                    arrow: {
                      sx: {
                        color: 'black',
                      },
                    },
                  }}
                >
                  <Image
                    src={`${ABS_URL}resources/img/vertic.png`}
                    alt="tick"
                    width={18}
                    height={18}
                  />
                </Tooltip>
              )}
            </span>
            <span
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                fontSize: 13,
                paddingTop: '3px',
                paddingBottom: '3px',
                lineHeight: '18px',
                fontWeight: 400,
              }}
            >
              {phone}
              {profileInfo?.verifiedNumber === 1 && (
                <Tooltip
                  placement="top"
                  title={
                    <Typography
                      variant="subtitle2"
                      sx={{
                        textAlign: 'center',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                        backgroundColor: 'black',
                        color: '#fff',
                        px: '2px',
                        borderRadius: '4px',
                        fontSize: 10,
                        fontWeight: 400,
                      }}
                    >
                      Phone Number Verified
                    </Typography>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'black',
                        color: '#fff',
                        p: '4px',
                      },
                    },
                    arrow: {
                      sx: {
                        color: 'black',
                      },
                    },
                  }}
                >
                  <Image
                    src={`${ABS_URL}resources/img/vertic.png`}
                    alt="tick"
                    width={18}
                    height={18}
                  />
                </Tooltip>
              )}
            </span>
            {verifyButton && (
              <Button
                sx={{
                  color: '#4b4b4b',
                  bgcolor: '#e5e6e7',
                  transition: '0.3s',
                  borderRadius: '20px',
                  fontSize: 12,
                  p: '6px 20px',
                  m: '5px 0px',
                  '&:hover': {
                    bgcolor: '#e5e6e7',
                  },
                }}
                onClick={() => {
                  openUrl('/settings?section=verify-contact-details');
                }}
              >
                {verifyButton}
              </Button>
            )}
          </Typography>
          <Box>
            <Typography>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: 11,
                  paddingRight: '10px',
                }}
              >
                <Image
                  src={`${ABS_URL}resources/img/clocknew.png`}
                  alt="clock"
                  width={14}
                  height={14}
                  style={{marginRight: '5px', marginBottom: '-2px'}}
                />
                {expyear} {expmonth}
              </span>
              {location && (
                <span style={{display: 'inline-block', fontSize: 11}}>
                  <Image
                    src={`${ABS_URL}resources/img/locationnew.png`}
                    alt="location"
                    width={10}
                    height={14}
                    style={{marginRight: '5px', marginBottom: '-2px'}}
                  />
                  {location}
                </span>
              )}
            </Typography>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                height: '1px',
                m: '15px 0px',
                width: '80%',
                background: 'silver',
              }}
            ></Box>
          </Box>
          <Box sx={{p: 0, m: 0}}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Image
                  width={30}
                  height={30}
                  src={Twitter}
                  alt="twitter"
                  style={{cursor: 'pointer'}}
                  onClick={() => {
                    openUrl(`${profileInfo?.twitter}`);
                  }}
                />
                <strong
                  style={{
                    borderRadius: '30px',
                    border: '1px solid #35414c',
                    fontWeight: 400,
                    color: '#4b4b4b',
                    fontSize: 12,
                    padding: '6px 12px',
                    display: 'inline-block',
                    width: '185px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    onSocialClick('Twitter', profileInfo?.twitter ?? '');
                  }}
                >
                  {profileInfo?.twitter ? 'Connected' : 'Connect'} with Twitter
                </strong>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Image
                  width={30}
                  height={30}
                  src={Facebook}
                  alt="facebook"
                  style={{cursor: 'pointer'}}
                  onClick={() => {
                    openUrl(`${profileInfo?.facebook}`);
                  }}
                />
                <strong
                  style={{
                    borderRadius: '30px',
                    border: '1px solid #35414c',
                    fontWeight: 400,
                    color: '#4b4b4b',
                    fontSize: 12,
                    padding: '6px 12px',
                    display: 'inline-block',
                    width: '185px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    onSocialClick('Facebook', profileInfo?.facebook ?? '');
                  }}
                >
                  {profileInfo?.facebook ? 'Connected' : 'Connect'} with
                  Facebook
                </strong>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Image
                  width={30}
                  height={30}
                  src={LinkedIn}
                  alt="linkedin"
                  style={{cursor: 'pointer'}}
                  onClick={() => {
                    openUrl(`${profileInfo?.linkedin}`);
                  }}
                />
                <strong
                  style={{
                    borderRadius: '30px',
                    border: '1px solid #35414c',
                    fontWeight: 400,
                    color: '#4b4b4b',
                    fontSize: 12,
                    padding: '6px 12px',
                    display: 'inline-block',
                    width: '185px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    onSocialClick('LinkedIn', profileInfo?.linkedin ?? '');
                  }}
                >
                  {profileInfo?.linkedin ? 'Connected' : 'Connect'} with
                  LinkedIn
                </strong>
              </Box>
            </Box>
            <Typography sx={{fontSize: 11, pt: '10px', fontStyle: 'italic'}}>
              We will never post to your profiles without your permission.
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box>
        <ProfileCardHarness
          variant={VersionVariants.VERSION_ONE}
          showSmallBadges={true}
          isMyProfilePage={true}
        />
      </Box>
    </Box>
  );
};

export default ProfileSection;
